import { PropsWithChildren, ReactNode, cloneElement, forwardRef } from 'react';

import { SponsorsFieldLayout } from '../Team/SponsorsFieldLayout';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { Maybe, Optional } from '@/types';
import { EnumTeamContext, PlayerDataComponentInField } from '@/types/team';
import { cn } from '@/utils';

type MainWrapper = PropsWithChildren<{
  onConfirmMobile?: ReactNode;
  context?: EnumTeamContext;
}>;

type FieldProps = {
  isMounted?: boolean;
  isLoading?: boolean;
  floatingCard?: ReactNode;
  backDropOpen?: boolean;
  backDropOnClick?: () => void;
  fieldPlayers: Optional<JSX.Element[]>;
  goalkeeper: Optional<Maybe<JSX.Element>>;
  selectedPositionOrden?: number;
  context?: EnumTeamContext;
};

type SidebarProps = PropsWithChildren<{
  disabled?: boolean;
}>;

const SideBarComponent = ({ children, disabled }: SidebarProps) => {
  return (
    <aside className="flex flex-col flex-1 min-w-[366px] overflow-x-hidden shadow-lg relative">
      {disabled && (
        <div className="absolute top-0 left-0 w-full h-full bg-black z-[1] opacity-80" />
      )}
      <section className="flex flex-col flex-1 bg-gdt-gray-7 relative">
        {children}
      </section>
    </aside>
  );
};

const MainWrapperComponent = ({
  children,
  onConfirmMobile,
  context,
}: MainWrapper) => {
  const { isMobile } = useIsDesktop();

  return (
    <section className="w-full flex flex-col lg:block text-center">
      <div
        className={cn(
          'lg:bg-[#70BA64] relative flex-1 lg:h-full flex flex-col items-center lg:block',
          {
            'overflow-hidden': context === EnumTeamContext.Build5,
          }
        )}
      >
        {children}
        {isMobile && <div className="mt-auto">{onConfirmMobile}</div>}
      </div>
    </section>
  );
};

const InnerWrapperComponent = ({ children }: PropsWithChildren) => {
  return (
    <div className="bg-[#70BA64] lg:bg-transparent p-3 lg:p-0 inline-block lg:block rounded-lg lg:mt-0">
      {children}
    </div>
  );
};

// eslint-disable-next-line react/display-name
const FieldComponent = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      floatingCard,
      isMounted,
      isLoading,
      context,
      goalkeeper,
      fieldPlayers,
      backDropOpen,
      backDropOnClick,
      selectedPositionOrden,
    },
    ref
  ) => {
    const { isDesktop } = useIsDesktop();

    return (
      <div
        className={cn('lg:w-auto relative max-w-[62.5rem] w-[18.75rem]', {
          'bg-fieldTeam lg:min-h-[47rem]': context === EnumTeamContext.Build5,
          'bg-fieldTeam5 lg:min-h-[46rem] max-w-100%':
            context === EnumTeamContext.Team5,
        })}
      >
        {/* sponsors cancha */}
        {isDesktop && isMounted && <SponsorsFieldLayout />}
        <div
          ref={ref}
          className={cn(
            'lg:max-w-[32.43rem] max-w-[18.75rem] m-auto flex flex-col relative lg:top-[100px] lg:-translate-x-1 lg:z-[2]',
            {
              'lg:top-[175px]': context === EnumTeamContext.Team5,
            }
          )}
        >
          {backDropOpen && (
            <div className="backdrop open" onClick={backDropOnClick} />
          )}
          <div className="wrapperPlayers h-[24.75rem] lg:h-[27rem] w-full lg:w-[150%] lg:left-[-25%] relative">
            {!isLoading && (
              <>
                <div className="m-auto lg:mt-7 flex justify-center">
                  {goalkeeper
                    ? cloneElement(goalkeeper, {
                        isSelected:
                          selectedPositionOrden !== undefined &&
                          (selectedPositionOrden === 0 ||
                            selectedPositionOrden === goalkeeper.props.data.id),
                      })
                    : null}
                </div>
                <div className="relative grid grid-cols-2 grid-rows-2 h-[232px] lg:h-[340px] mt-5 lg:mt-0">
                  {fieldPlayers?.map((player, index) => {
                    const playerData =
                      player?.props as PlayerDataComponentInField;

                    const isSelected =
                      selectedPositionOrden !== undefined &&
                      (selectedPositionOrden === playerData?.data?.orden ||
                        selectedPositionOrden === playerData?.data?.id);

                    return cloneElement(player, {
                      className: 'place-self-center',
                      key: `fieldplayer-${index}`,
                      isSelected,
                    });
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        {isMounted && floatingCard}
      </div>
    );
  }
);

export const FieldLayout = {
  Sidebar: SideBarComponent,
  Main: MainWrapperComponent,
  Inner: InnerWrapperComponent,
  Field: FieldComponent,
};
